"use client";

import React, { useState } from "react";
import Link from "next/link";
import Twitter from "@/public/assets/icons/SocialMedia/Twitter.svg";
import Discord from "@/public/assets/icons/SocialMedia/Discord.svg";
import Instagram from "@/public/assets/icons/SocialMedia/Instagram.svg";
import Logo from "@/public/assets/icons/LeftSidebar/Logo.svg";
import { useToast } from "@/components/ui/use-toast";
import { Icons } from "@/components/(new)/shared/helper/Icons";

const Footer = () => {
  const productLinks = [
    { name: "Disclaimer", href: "/disclaimer" },
    { name: "Legal", href: "/legal" },
    { name: "Blogs", href: "/blog" },
    { name: "Pricing", href: "/pricing" },
  ];

  const toolLinks = [
    // { name: "Image editor", href: "/image-editor" },
    { name: "Image generator", href: "/text-to-Image" },
    { name: "Vector Generator", href: "/vector-generator" },
    // { name: "Video Generator", href: "/video-generator" },
    { name: "Background Remover", href: "/background-Remover" },
    // { name: "Bulk Bg Remover", href: "/bulk-bg-remover" },
  ];

  const legalLinks = [
    { name: "Privacy Policies", href: "/privacy-policy" },
    { name: "Terms", href: "/terms" },
  ];

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Subscribed Successfully! 🎉",
          description: "You've been successfully subscribed to our newsletter.",
          variant: "default",
          className: "font-medium border-2",
        });
        setEmail("");
      } else {
        const errorMessage =
          data.error?.message ||
          data.message ||
          "Failed to subscribe to newsletter";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Failed ❌",
        description:
          error instanceof Error
            ? error.message
            : "Failed to subscribe to newsletter",
        className: "font-medium border-2",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="w-full bg-black px-[37px] py-3 md:px-[100px]">
      <div className="flex w-full flex-col items-start justify-between gap-[50px] md:mt-5 md:mb-10 md:items-center md:gap-[30px]">
        <div className="flex w-full flex-col gap-[50px] md:flex-row md:justify-between md:gap-0">
          {/* Product and Tools Section */}
          <div className="flex justify-between max-md:w-full md:items-start md:gap-[60px]">
            {/* Product Links */}
            <div className="inline-flex flex-col items-start justify-start gap-3">
              <div className="text-base font-semibold text-white md:text-lg">
                Product
              </div>
              <div className="flex flex-col items-start justify-start gap-1.5">
                {productLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="text-xs leading-[15px] font-medium text-[#b3b3b3] transition-opacity hover:opacity-80 md:text-sm md:leading-[17px]"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Tools Links */}
            <div className="inline-flex flex-col items-start justify-start gap-3">
              <div className="text-base font-semibold text-white md:text-lg">
                More Tools
              </div>
              <div className="flex flex-col items-start justify-start gap-1.5">
                {toolLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="text-xs leading-[15px] font-medium text-[#b3b3b3] transition-opacity hover:opacity-80 md:text-sm md:leading-[17px]"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Subscribe Section */}
          <div className="flex w-[300px] flex-col items-start justify-start gap-1.5 md:w-auto md:items-center md:items-start md:gap-3">
            <div className="text-xl font-semibold text-white md:text-[28px]">
              Subscribe
            </div>
            <form onSubmit={handleSubmit} className="w-full">
              <div className="flex w-full gap-0.5">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 flex-1 rounded-tl-md rounded-bl-md border-2 border-[#303437] bg-[#14181C]/5 px-4 text-xs font-medium text-white shadow-[1.46px_1.46px_10.94px_0px_rgba(0,0,0,0.25)] outline-2 outline-offset-[-2px] outline-white/10 placeholder:text-[#8c8c8c] md:h-[52px] md:px-5 md:text-base md:placeholder:text-[#bcbcbc] lg:w-[306px]"
                  disabled={loading}
                  autoComplete="email"
                  autoCorrect="off"
                  autoCapitalize="off"
                  required
                  placeholder="Your Email Address"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="h-10 w-10 rounded-tr-md rounded-br-md bg-[#6cb4ff] shadow-[1.46px_1.46px_10.94px_0px_rgba(0,0,0,0.25)] outline-1 outline-offset-[-1px] outline-white/5 md:h-[52px] md:w-[52px]"
                >
                  <Icons.arrow className="mx-auto h-5 w-5 text-black" />
                </button>
              </div>
            </form>
            <div className="text-xs leading-[15px] font-medium text-[#BFBFBF] md:max-w-[472px] md:text-sm md:leading-tight">
              Be the First to Know About New Features.
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-0 w-full outline-[0.50px] outline-offset-[-0.25px] outline-white/30" />

        {/* Bottom Section */}
        <div className="flex w-full flex-col items-center justify-between gap-[30px]">
          {/* Social Links */}
          <div className="flex-between w-full">
            <div className="flex-center gap-2 md:gap-[30px]">
              {[
                { href: "https://x.com/bundleddesign", Icon: Twitter },
                {
                  href: "https://www.instagram.com/bundled.design",
                  Icon: Instagram,
                },
                { href: "https://discord.gg/EQfgeJTjKK", Icon: Discord },
              ].map((link, index) => (
                <Link
                  key={index}
                  href={link.href}
                  className="size-5 text-white transition-opacity hover:opacity-80 md:size-[30px]"
                  aria-label={`Visit our ${link.Icon.name} page`}
                >
                  <link.Icon className="size-full" />
                </Link>
              ))}
            </div>

            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 max-md:hidden">
              <Logo className="size-[25.87px] md:size-7" />
              <span className="font-worksans text-2xl font-normal text-white">
                Bundled.design
              </span>
            </Link>

            {/* Legal Links */}
            <div className="flex w-full items-center justify-end gap-[30px] md:w-auto">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-white underline transition-opacity hover:opacity-80 md:text-base"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 md:hidden">
            <Logo className="size-[25.87px] md:size-7" />
            <span className="font-worksans text-2xl font-normal text-white">
              Bundled.design
            </span>
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
